import React, { useMemo } from 'react';
import type { CanvasFrame } from '../types';

interface Props {
  frames: CanvasFrame[];
  connections: string[][];
  board: { width: number; height: number };
  /** Alleen zichtbaar tijdens overzicht (sectie 9) -- geen afleiding tijdens
   * diepe focus. */
  visible: boolean;
}

function frameCenter(frame: CanvasFrame) {
  return { x: frame.x + frame.width / 2, y: frame.y + frame.height / 2 };
}

// Gebogen verbinding tussen twee frames (sectie 9: "gebogen SVG-paden"), de
// boog buigt weg van het midden van het bord voor een organisch,
// "uitwaaierend" resultaat i.p.v. rechte lijnen die elkaar kruisen -- 1:1
// poort van het prototype's curvedPathBetween().
function curvedPathBetween(a: CanvasFrame, b: CanvasFrame, board: { width: number; height: number }): string {
  const ca = frameCenter(a);
  const cb = frameCenter(b);
  const mx = (ca.x + cb.x) / 2;
  const my = (ca.y + cb.y) / 2;
  const dx = cb.x - ca.x;
  const dy = cb.y - ca.y;
  const len = Math.hypot(dx, dy) || 1;
  const px = -dy / len;
  const py = dx / len;
  const outX = mx - board.width / 2;
  const outY = my - board.height / 2;
  const sign = px * outX + py * outY >= 0 ? 1 : -1;
  const bow = len * 0.12 * sign;
  const cx = mx + px * bow;
  const cy = my + py * bow;
  return `M ${ca.x} ${ca.y} Q ${cx} ${cy} ${cb.x} ${cb.y}`;
}

export default function CanvasConnectionsLayer({ frames, connections, board, visible }: Props) {
  const framesById = useMemo(() => {
    const map = new Map<string, CanvasFrame>();
    frames.forEach((frame) => map.set(frame.id, frame));
    return map;
  }, [frames]);

  return (
    <svg
      className="canvas-connections"
      width={board.width}
      height={board.height}
      viewBox={`0 0 ${board.width} ${board.height}`}
      style={{ opacity: visible ? 1 : 0 }}
    >
      {connections.map(([fromId, toId]) => {
        const a = framesById.get(fromId);
        const b = framesById.get(toId);
        if (!a || !b) return null;
        return <path key={`${fromId}-${toId}`} className="canvas-connection-line" d={curvedPathBetween(a, b, board)} />;
      })}
    </svg>
  );
}
